import React, { useState, useRef } from 'react';
import { Maximize2, Search } from 'lucide-react';

interface ProductImageZoomProps {
  src: string;
  alt: string;
  onImageClick?: () => void;
  brandBadge?: string;
}

export const ProductImageZoom: React.FC<ProductImageZoomProps> = ({
  src,
  alt,
  onImageClick,
  brandBadge,
}) => {
  const [isHovering, setIsHovering] = useState(false);
  const [coords, setCoords] = useState<{ x: number; y: number }>({ x: 50, y: 50 });
  const containerRef = useRef<HTMLDivElement>(null);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const rawX = ((e.clientX - rect.left) / rect.width) * 100;
    const rawY = ((e.clientY - rect.top) / rect.height) * 100;
    // Acotar entre 0% y 100%
    const x = Math.max(0, Math.min(100, rawX));
    const y = Math.max(0, Math.min(100, rawY));
    setCoords({ x, y });
  };

  const handleMouseEnter = () => {
    if (typeof window !== 'undefined' && window.matchMedia('(hover: hover)').matches) {
      setIsHovering(true);
    }
  };

  return (
    <div
      ref={containerRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setIsHovering(false)}
      onMouseMove={handleMouseMove}
      onClick={onImageClick}
      className="relative bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-2xl p-3 sm:p-6 lg:p-8 flex items-center justify-center overflow-hidden cursor-zoom-in select-none group shadow-sm transition-all min-h-[260px] sm:min-h-[360px] lg:min-h-[400px]"
      title="Clic para abrir la galería en pantalla completa"
    >
      {/* Imagen con Zoom Suave Centrado en Coordenadas */}
      <img
        src={src}
        alt={alt}
        className="w-full h-60 sm:h-80 lg:h-96 object-contain rounded-lg transition-transform duration-100 ease-out pointer-events-none"
        style={{
          transform: isHovering ? 'scale(2.2)' : 'scale(1)',
          transformOrigin: `${coords.x}% ${coords.y}%`,
        }}
        loading="eager"
      />

      {/* Badge de Marca */}
      {brandBadge && (
        <div className="absolute top-3 left-3 sm:top-4 sm:left-4 z-10 pointer-events-none transition-opacity duration-200 group-hover:opacity-40">
          <span className="px-2.5 py-1 rounded-md bg-neutral-900/80 dark:bg-neutral-800/80 text-white text-[10px] sm:text-xs font-black tracking-wider uppercase backdrop-blur-sm shadow-sm border border-neutral-700/60">
            {brandBadge}
          </span>
        </div>
      )}

      {/* Botón Flotante de Ampliar Galería */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onImageClick?.();
        }}
        className="absolute top-3 right-3 sm:top-4 sm:right-4 z-10 p-2 sm:p-2.5 rounded-xl bg-white/90 dark:bg-neutral-800/90 text-neutral-700 dark:text-neutral-300 hover:text-brand-primary dark:hover:text-brand-primary shadow-md border border-neutral-200/80 dark:border-neutral-700/80 transition-all hover:scale-110 cursor-pointer"
        aria-label="Abrir galería de fotos en pantalla completa"
        title="Ver galería completa"
      >
        <Maximize2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
      </button>

      {/* Indicador de ayuda al usuario (oculto durante zoom para no tapar hardware) */}
      <div
        className={`absolute bottom-2.5 sm:bottom-3 left-1/2 -translate-x-1/2 z-10 pointer-events-none transition-opacity duration-300 ${
          isHovering ? 'opacity-0' : 'opacity-90 group-hover:opacity-100'
        }`}
      >
        <div className="flex items-center gap-1.5 px-3 py-1 sm:py-1.5 rounded-full bg-neutral-900/85 dark:bg-neutral-800/90 text-white text-[10px] sm:text-[11px] font-medium backdrop-blur-md shadow-lg border border-neutral-700/50 whitespace-nowrap">
          <Search className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-brand-primary" />
          <span className="hidden sm:inline">Pasa el cursor para zoom • Clic para ampliar</span>
          <span className="sm:hidden">Toca para ampliar galería</span>
        </div>
      </div>
    </div>
  );
};
